import { Briefcase, MapPin, Users } from 'lucide-react';
import type { ProgramSystem } from '@/modules/governmentIntake/context/intake-form-context';

export const formSidebarGroups = {
  programs: [
    { id: 1, label: 'Program Details' },
    { id: 2, label: 'Eligibility' },
    { id: 3, label: 'Funding' },
  ],

  projects: [
    { id: 1, label: 'Project Overview' },
    { id: 2, label: 'Timeline' },
    { id: 3, label: 'Budget' },
  ],

  properties: [
    { id: 1, label: 'Property Info' },
    { id: 2, label: 'Ownership' },
    { id: 3, label: 'Legal' },
  ],
} as const;

export const govtIntakeFormSections = [
  { id: 1, name: 'Entry & Contact Info', completed: false },
  { id: 2, name: 'Role & Context', completed: false },
  { id: 3, name: 'Current Systems', completed: false },
  { id: 4, name: 'Primary Object of Work', completed: false },
  { id: 5, name: 'Needs', completed: false },
  { id: 6, name: 'Current Tools Used', completed: false },
  { id: 7, name: 'Legacy Data', completed: false },
  { id: 8, name: 'Program Structure', completed: false },
  { id: 9, name: 'Program Operations', completed: false },
  { id: 10, name: 'Program Pain Points', completed: false },
  { id: 11, name: 'Review & Submit', completed: false },
];

export const govtIntakeFormRole = [
  { id: 'operator', name: 'Day-to-day operator (hands-on execution)' },
  { id: 'manager', name: 'Department manager (oversees staff and workflows)' },
  { id: 'dept-head', name: 'Department head (sets priorities and direction)' },
  { id: 'executive', name: 'Executive / leadership (oversight and accountability)' },
  { id: 'it-data', name: 'IT or data support (systems, integrations, reporting)' },
  { id: 'consultant', name: 'External consultant or advisor' },
];

export const govtIntakeFormOrganizationType = [
  { id: 'village', name: 'Village', completed: false },
  { id: 'cityTown', name: 'City / Town', completed: false },
  { id: 'county', name: 'County', completed: false },
  { id: 'regionalAuthority', name: 'Regional authority or district', completed: false },
  { id: 'stateAgency', name: 'State agency', completed: false },
  { id: 'other', name: 'Other', completed: false },
];

export const govtIntakeFormState = [
  { name: 'Alabama', abbreviation: 'AL' },
  { name: 'Alaska', abbreviation: 'AK' },
  { name: 'Arizona', abbreviation: 'AZ' },
  { name: 'Arkansas', abbreviation: 'AR' },
  { name: 'California', abbreviation: 'CA' },
  { name: 'Colorado', abbreviation: 'CO' },
  { name: 'Connecticut', abbreviation: 'CT' },
  { name: 'Delaware', abbreviation: 'DE' },
  { name: 'Florida', abbreviation: 'FL' },
  { name: 'Georgia', abbreviation: 'GA' },
  { name: 'Hawaii', abbreviation: 'HI' },
  { name: 'Idaho', abbreviation: 'ID' },
  { name: 'Illinois', abbreviation: 'IL' },
  { name: 'Indiana', abbreviation: 'IN' },
  { name: 'Iowa', abbreviation: 'IA' },
  { name: 'Kansas', abbreviation: 'KS' },
  { name: 'Kentucky', abbreviation: 'KY' },
  { name: 'Louisiana', abbreviation: 'LA' },
  { name: 'Maine', abbreviation: 'ME' },
  { name: 'Maryland', abbreviation: 'MD' },
  { name: 'Massachusetts', abbreviation: 'MA' },
  { name: 'Michigan', abbreviation: 'MI' },
  { name: 'Minnesota', abbreviation: 'MN' },
  { name: 'Mississippi', abbreviation: 'MS' },
  { name: 'Missouri', abbreviation: 'MO' },
  { name: 'Montana', abbreviation: 'MT' },
  { name: 'Nebraska', abbreviation: 'NE' },
  { name: 'Nevada', abbreviation: 'NV' },
  { name: 'New Hampshire', abbreviation: 'NH' },
  { name: 'New Jersey', abbreviation: 'NJ' },
  { name: 'New Mexico', abbreviation: 'NM' },
  { name: 'New York', abbreviation: 'NY' },
  { name: 'North Carolina', abbreviation: 'NC' },
  { name: 'North Dakota', abbreviation: 'ND' },
  { name: 'Ohio', abbreviation: 'OH' },
  { name: 'Oklahoma', abbreviation: 'OK' },
  { name: 'Oregon', abbreviation: 'OR' },
  { name: 'Pennsylvania', abbreviation: 'PA' },
  { name: 'Rhode Island', abbreviation: 'RI' },
  { name: 'South Carolina', abbreviation: 'SC' },
  { name: 'South Dakota', abbreviation: 'SD' },
  { name: 'Tennessee', abbreviation: 'TN' },
  { name: 'Texas', abbreviation: 'TX' },
  { name: 'Utah', abbreviation: 'UT' },
  { name: 'Vermont', abbreviation: 'VT' },
  { name: 'Virginia', abbreviation: 'VA' },
  { name: 'Washington', abbreviation: 'WA' },
  { name: 'West Virginia', abbreviation: 'WV' },
  { name: 'Wisconsin', abbreviation: 'WI' },
  { name: 'Wyoming', abbreviation: 'WY' },
];

export const govtIntakeFormDepartments = [
  {
    id: 'building',
    title: 'Building & Code',
  },
  {
    id: 'planning',
    title: 'Planning & Zoning',
  },
  {
    id: 'housing',
    title: 'Housing',
  },
  {
    id: 'economic',
    title: 'Economic Development',
  },
  {
    id: 'public_works',
    title: 'Public Works',
  },
  {
    id: 'finance',
    title: 'Finance',
  },
  {
    id: 'executive_office',
    title: "Mayor / City Manager's Office",
  },
  {
    id: 'it_gis',
    title: 'IT / GIS',
  },
  {
    id: 'other',
    title: 'Other',
  },
];

export const govtIntakeFormProvideRecommendations = [
  {
    id: 'approvalAuthority',
    title: 'Yes, I have approval authority',
  },
  {
    id: 'provideRecommendation',
    title: 'Yes, I provide recommendations',
  },
  {
    id: 'notApprovals',
    title: 'No, I am not involved in approvals',
  },
];

export const govtIntakeFormContactMethod = [
  {
    id: 'email',
    title: 'Email',
  },
  {
    id: 'phone',
    title: 'Phone',
  },
];

export const govtIntakeFormInteractions = [
  {
    id: 'daily',
    title: 'Daily',
  },
  {
    id: 'weekly',
    title: 'Weekly',
  },
  {
    id: 'monthly',
    title: 'Monthly',
  },
  {
    id: 'rarely',
    title: 'Rarely',
  },
];

export const govtIntakeFormUsers = [
  {
    id: 'user',
    title: 'End user',
  },
  {
    id: 'manager',
    title: 'Manager',
  },
  {
    id: 'decisionMaker',
    title: 'Decision-maker',
  },
  {
    id: 'support',
    title: 'Supporting this initiative',
  },
];

export const govtIntakeFormApprovalRecommendations = [
  {
    id: 'yes',
    title: 'Yes',
  },
  {
    id: 'no',
    title: 'No',
  },
  {
    id: 'sometimes',
    title: 'Sometimes',
  },
];

export const govtIntakeFormApprovalRecommendations1 = [
  {
    id: 'approvalAuthority',
    title: 'Yes, I have approval authority',
  },
  {
    id: 'recommendationAuthority',
    title: 'Yes, I provide recommendations',
  },
  {
    id: 'noApprovalAuthority',
    title: 'No, I am not involved in approvals',
  },
];

export const govtIntakeFormReporting = [
  {
    id: 'yes',
    title: 'Yes',
  },
  {
    id: 'no',
    title: 'No',
  },
  {
    id: 'notSure',
    title: 'Not Sure',
  },
];

export const govtIntakeFormAccessMethod = [
  { id: 'screen', label: 'Screen Only' },
  { id: 'export', label: 'Export (CSV/PDF)' },
  { id: 'api', label: 'API' },
  { id: 'unsure', label: 'Unsure' },
];

export const govtIntakeFormNonSystemTool = [
  { id: 'spreadsheet', label: 'Heavy spreadsheet use', enabled: false },
  { id: 'drives', label: 'Shared drives / PDFs', enabled: false },
  { id: 'email', label: 'Email-based workflows', enabled: false },
  { id: 'custom', label: 'Custom or legacy tools', enabled: false },
];

export const govtIntakeFormPrimaryObjectOfWork = [
  {
    id: 'programs',
    icon: Users,
    title: 'Programs',
    description: 'Ongoing initiatives involving funding, participants, or compliance',
    examples: ['Down payment assistance', 'Main Street grants', 'ARPA programs'],
  },
  {
    id: 'projects',
    icon: Briefcase,
    title: 'Projects',
    description: 'Time-bound efforts with defined outcomes',
    examples: ['Redevelopment projects', 'capital improvements'],
  },
  {
    id: 'properties',
    icon: MapPin,
    title: 'Properties',
    description: 'Individual parcels or buildings tracked or regulated',
    examples: ['Inspections, code enforcement', 'land bank inventory'],
  },
];

export const govtIntakeFormProgramNeeds = ['ProjectSubNeeds', 'Quick qualification of platform requirements for program management.'];

export const govtIntakeFormProgramTypeOptions = [
  { id: 'payment', label: 'Down payment assistance' },
  { id: 'repair', label: 'Home repair / rehab' },
  { id: 'facade', label: 'Façade / Main Street' },
  { id: 'business', label: 'Small business grant / loan' },
  { id: 'arpa', label: 'ARPA / CDBG / HOME' },
  { id: 'other', label: 'Other' },
];

export const govtIntakeFormHowItIsUsed = [
  { label: 'System of record', key: 'systemOfRecord' },
  { label: 'Reference only', key: 'referenceOnly' },
  { label: 'Reporting', key: 'reporting' },
  { label: 'Intake', key: 'intake' },
  { label: 'Storage', key: 'storage' },
];

export const govtIntakeFormCurrentSystems = [
  {
    id: 'softwareName',
    name: 'Software name',
    placeholder: 'e.g., Tyler Munis, Excel, GIS system...',
  },
  {
    id: 'vendor',
    name: 'Vendor',
    placeholder: 'e.g., Tyler Technologies, Microsoft...',
  },
  {
    id: 'primaryUse',
    name: 'Primary use',
    placeholder: 'e.g., Property tracking, permit management...',
  },
  {
    id: 'department',
    name: 'Department',
    placeholder: 'e.g., Planning, Building & Code...',
  },
  {
    id: 'dataStored',
    name: 'Type of data stored',
    placeholder: 'e.g., Parcel data, permit history, inspection records...',
  },
];

export const govtIntakeFormLegacyDataLiveToday = [
  { id: 'spreadsheets', label: 'Spreadsheets' },
  { id: 'pdfs', label: 'PDFs/scans' },
  { id: 'drives', label: 'Shared drives' },
  { id: 'codeEnforcePermittingSystem', label: 'Code enforcement / permitting system' },
  { id: 'software', label: 'Software systems' },
  { id: 'pmtools', label: 'Project Management tools' },
  { id: 'email', label: 'Email' },
  { id: 'other', label: 'Other' },
];

export const govtIntakeFormHideByTitle = {
  program: ['pmtools'],
  projects: ['pmtools', 'other'],
};

export const govtIntakeFormLegacyDataQuality = [
  { id: 'clean', label: 'Clean' },
  { id: 'mixed', label: 'Mixed' },
  { id: 'messy', label: 'Messy' },
  { id: 'unknown', label: 'Unknown' },
];

export const govtIntakeFormTrackingMethodOptions = [
  { id: 'spreadsheet', label: 'Spreadsheet' },
  { id: 'software', label: 'Software' },
  { id: 'paper', label: 'Paper' },
  { id: 'mixed', label: 'Mixed' },
];
export const govtIntakeFormTiedToOptions = [
  { id: 'properties', label: 'Properties' },
  { id: 'geography', label: 'Geography' },
  { id: 'incomeEligibility', label: 'Income eligibility' },
  { id: 'businessType', label: 'Business type' },
];

export const govtIntakeFormPropertyTypeManaged = [
  { id: 'residential', label: 'Residential' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'municipal', label: 'Municipal buildings' },
  { id: 'vacant', label: 'Vacant lots' },
  { id: 'Other', label: 'Other' },
];

export const govtIntakeFormHowOftenUpdated = [
  { id: 'realTime', label: 'Real-time / Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'Ad-hoc', label: 'Ad-hoc / As needed' },
];

export const govtIntakeOrganizationType = [
  {
    id: 'micro',
    label: 'Micro',
    minStaff: 1,
    maxStaff: 9,
    description: 'Startup / sole operation',
  },
  {
    id: 'small',
    label: 'Small',
    minStaff: 10,
    maxStaff: 49,
    description: 'Small business',
  },
  {
    id: 'medium',
    label: 'Medium',
    minStaff: 50,
    maxStaff: 249,
    description: 'Mid-sized enterprise',
  },
  {
    id: 'large',
    label: 'Large',
    minStaff: 250,
    maxStaff: 999,
    description: 'Large organization',
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    minStaff: 1000,
    maxStaff: null,
    description: 'Corporation / multinational',
  },
];

/** Mock pricing breakdown by module (Sherpa, Rover, Atlas) for admin detail view */
export const govtModulePricingBreakdown = [
  {
    name: 'Annual Budget',
    category: 'budget',
    total: 12500000,
    features: [],
  },
  {
    name: 'SEIN',
    category: 'Terminal',
    total: 24000,
    features: [],
  },
  {
    name: 'Sherpa',
    category: 'Project Management',
    total: 18000,
    tiers: [
      { name: 'Starter', cost: 1000 },
      { name: 'Professional', cost: 2500 },
      { name: 'Enterprise', cost: 6000 },
    ],
    features: [
      { name: 'Budget Tracking', cost: 3000 },
      { name: 'Timeline Visualization', cost: 3000 },
      { name: 'Advanced Reporting', cost: 3000 },
    ],
  },
  {
    name: 'Rover',
    category: 'Public Forms',
    total: 15000,
    tiers: [
      { name: 'Starter', cost: 2000 },
      { name: 'Standard', cost: 5000 },
      { name: 'Professional', cost: 8000 },
    ],
    features: [
      { name: 'Workflow Engine', cost: 2500 },
      { name: 'Email Notifications', cost: 2500 },
      { name: 'Form Analytics', cost: 2500 },
    ],
  },
  {
    name: 'Atlas',
    category: 'Property Management',
    total: 12000,
    tiers: [
      { name: 'Standard', cost: 1000 },
      { name: 'Professional', cost: 2500 },
      { name: 'Enterprise', cost: 9000 },
    ],
    features: [
      { name: 'GIS Integration', cost: 2000 },
      { name: 'Inspection Workflow', cost: 2000 },
    ],
  },
];

/** Annual total for pricing summary (computed or placeholder) */
export const annualTotalCost = 0;

export const govtIntakeModuleUploadInstruction =
  'Please provide as many examples of the reports and applications you would like digitized. This helps us accurately scope your implementation.';
export const govtIntakeModuleUploadExistingReports = 'Upload existing reports for digitization (optional)';
export const govtIntakeModuleUploadExistingForms = 'Upload existing forms for digitization (optional)';

export const govtIntakeModuleExistingSystems: ProgramSystem[] = [
  { id: '1', name: 'Microsoft Excel', vendor: 'Microsoft', notes: '' },
  { id: '2', name: 'Microsoft Sharepoint', vendor: 'Microsoft', notes: '' },
  { id: '3', name: 'Laserfiche', vendor: 'Laserfiche', notes: '' },
  { id: '4', name: 'Tyler EnerGov', vendor: 'Tyler Technologies', notes: '' },
  { id: '5', name: 'Accela', vendor: 'Accela', notes: '' },
  { id: '6', name: 'Granicus', vendor: 'Granicus', notes: '' },
  { id: '7', name: 'OpenGov', vendor: 'OpenGov', notes: '' },
  { id: '8', name: 'Cityworks', vendor: 'Cityworks', notes: '' },
  { id: '9', name: 'Smartsheet', vendor: 'Smartsheet', notes: '' },
  { id: '10', name: 'Monday.com', vendor: 'Monday.com', notes: '' },
  { id: '11', name: 'Munis', vendor: 'Tyler Technologies', notes: '' },
];
